/**
 * public-init.js — Punto de entrada para páginas públicas (server-rendered).
 *
 * Este archivo es INDEPENDIENTE de script.js (el bundle del editor).
 * Solo importa las animaciones, el resolvedor de Unsplash y las
 * dependencias de vendor (Lenis, Splide) necesarias para que la
 * página pública se vea y se comporte igual que en el editor.
 *
 * NO importa: context-menu, tools, dragdrop, catalog, history, state,
 * ni accede a #app-config, #datos-web.
 */

// --- STORE (estado mínimo: editorMode = false) ---
import { store } from '../core/store.js';

// --- I18N: idioma de las etiquetas de UI (chrome) pintadas en RUNTIME ---
import { setRenderLang, t } from '../utils/i18n.js';

// --- CORE: hooks de post-render + offset del header fixed + landmarks de accesibilidad ---
import { runPublicPostRenderHooks, applyFirstSectionHeaderOffset, applyPublicLandmarks } from '../core/render.js';

// --- BLOQUE LIBRE (ai-custom): instala el puente de altura del iframe sandbox.
//     El HTML compilado del sitio público contiene el <iframe>; este import
//     activa el listener que ajusta su alto según el contenido. ---
import '../blocks/ai-custom.js';
// …y relanza por script la carga de esos iframes (utils/iframes-ia.js: con dos horneados, la
// navegación que arranca el PARSER se colgaba en Chrome de escritorio y la página no cargaba).
import { relanzarIframesIA } from '../utils/iframes-ia.js';

// --- RESOLVEDOR DE IMÁGENES ---
import { resolverImagenesUnsplash } from '../editor/unsplash.js';

// --- ENLACES A OTRA PÁGINA (`?page=<id>[#ancla]`), mismo parseo que el editor ---
import { destinoDePagina } from '../utils/links.js';

// --- VENDOR WRAPPERS ---
import { initSmoothScroll } from '../animations/smooth-scroll.js';
import { initSplideCarousels } from '../animations/splide-init.js';

// --- DELEGACIÓN DE EVENTOS DE BLOQUES (menú/vídeo/CTA/mapa por CLASE) ---
// Sustituye a los on* inline borrados de los renderers: sin esto, el HTML
// publicado y saneado (sin on*) no tendría interacción.
import { initBlockActions } from '../core/block-actions.js';

// --- AUTO-REGISTRO DE ANIMACIONES (se registran como postRenderHooks) ---
import '../animations/counter.js';
import '../animations/bento-scenes-anim.js';
import '../animations/split-text.js';
import '../animations/svg-draw.js';
import '../animations/typed.js';
import '../animations/parallax.js';
import '../animations/whatsapp.js';
import '../animations/interactive.js';
import '../animations/countdown.js';
import '../animations/backgrounds.js';
import '../animations/anime-orchestrador.js';
import '../animations/form-submit.js';   // envío del bloque form (mailto/wa, MVP)

// --- ENTORNO: siempre modo lectura ---
store.set('editorMode', false);

// Idioma de las etiquetas de UI (chrome) que se pintan en RUNTIME: botón flotante de WhatsApp,
// asunto del mailto del bloque form… El chrome HORNEADO ya viene resuelto por compileSiteHtml
// (que llama a setRenderLang antes de compilar), pero lo que se crea en cliente necesita el
// idioma de ESTA página. Lo da <html lang>, que page_detail.html emite por página (incluidas las
// espejo '-xx' vía lang_of_slug). Sin esto, t() caería siempre al español.
setRenderLang(document.documentElement.lang || 'es');

// ═══════════════════════════════════════════════════════════════
//  SHIMMING: El orquestador de animaciones y las variant-animations
//  están diseñados para funcionar con la estructura del editor:
//
//    <div class="block-editor-wrapper" data-anim="fade-up" data-type="hero">
//        <section id="bloque-hero" class="reveal variant-v1" ...>
//
//  En la página pública, las secciones no tienen wrapper. Este shim
//  envuelve cada <section>/<header>/<footer> en un div con los
//  data attributes necesarios, replicando la estructura del editor.
//  Esto evita parchear las 17+ variant animations individuales.
//
//  En producción, #resultado envuelve todo (header_block, content_block,
//  footer_block), por lo que buscamos los bloques dentro de sus
//  respectivos contenedores nube3i. Fallback a hijos directos para
//  compatibilidad con el editor/preview.
// ═══════════════════════════════════════════════════════════════
(function shimBlockWrappers() {
    const root = document.getElementById('resultado');
    if (!root) return;

    // Añadir clase preview-mode al body para que las reglas CSS del header
    // apliquen correctamente una vez hoisted fuera de #resultado.
    document.body.classList.add('preview-mode');

    // TODA la cirugía del shim (envolver → hoist del header → landmarks) corre con los iframes
    // del bloque IA DETENIDOS, y se relanzan por script en la tarea siguiente
    // (utils/iframes-ia.js, gotcha 49): la navegación `about:srcdoc` que arrancó el parser se
    // cuelga en Chrome de escritorio cuando hay dos, y lanzada por script tras el shim no. Llega
    // a lo YA publicado sin republicar: este bundle se sirve con `?v={{ version }}`. (El cuerpo va
    // SIN reindentar a propósito: es byte a byte el de antes y así el diff enseña solo el envoltorio.)
    relanzarIframesIA(root, () => {
    const blocks = [];

    // DÓNDE ATERRIZA EL HTML DEL BUILDER (lo que hacía fallar a header y footer):
    // `page_detail.html` sirve el `page_html` compilado DENTRO de `#nube3i_content_block`, y
    // ahí va el bloque entero — header, secciones y footer. Los otros dos contenedores
    // (`#nube3i_header_block` / `#nube3i_footer_block`) son el chrome del ERP y en un sitio
    // del builder van vacíos. Las secciones ya miraban en el sitio correcto; header y footer
    // no, así que se quedaban FUERA de `blocks`: sin shim, sin que su `data-anim` se moviera
    // al wrapper (gotcha #18) y —lo que se veía— sin el hoist del header, que es quien llama
    // a `applyFirstSectionHeaderOffset`. Resultado: en una página que NO abre con hero (una
    // subpágina de formulario, menú o contenido) el header fijo tapaba su primera sección.
    // No saltaba a la vista porque casi todas las páginas abren con hero, y el hero reserva
    // su propio hueco por CSS (`.hero-section`).
    const contentContainer = root.querySelector('#nube3i_content_block');
    // Sin `#nube3i_content_block` (maquetación antigua) el ámbito es `root` y las dos
    // búsquedas quedan EXACTAMENTE como estaban: esto solo añade un sitio donde mirar.
    const ambitoBuilder = contentContainer || root;

    // Header: contenedor del ERP, o el del builder (que es donde está de verdad).
    const header = root.querySelector('#nube3i_header_block > header')
                || ambitoBuilder.querySelector(':scope > header');
    if (header) blocks.push(header);

    // Sections (contenido): buscar dentro del contenedor nube3i o como hijos directos.
    // `:scope > main > section` cubre el landmark <main id="contenido"> que compileSiteHtml
    // añade al HTML publicado (accesibilidad + destino del skip-link). Las dos ramas
    // conviven a propósito: las páginas YA publicadas antes de ese cambio siguen siendo una
    // pila PLANA de <section> y perderían todas las animaciones si solo se mirase dentro
    // de <main>. Solo hijos DIRECTOS en ambos casos → nunca envuelve una <section> anidada.
    const SEL_SECCIONES = ':scope > section, :scope > main > section';
    const sections = ambitoBuilder.querySelectorAll(SEL_SECCIONES);
    sections.forEach(s => blocks.push(s));

    // Footer: mismo caso que el header (vive en el contenedor del builder). Sin shim no
    // recibía `.anime-init` en un wrapper, así que además de no animarse se quedaban sin
    // pintar las reglas de DESCENDENCIA de site.css que cuelgan de él (gotcha #18).
    const footer = root.querySelector('#nube3i_footer_block > footer')
                || ambitoBuilder.querySelector(':scope > footer');
    if (footer) blocks.push(footer);

    blocks.forEach((el, index) => {
        // Extraer tipo de bloque del id (ej: "bloque-hero" → "hero",
        // "bloque-process-3" → "process", "bloque-cta-bar" → "cta-bar")
        const id = el.id || '';
        const match = id.match(/^bloque-(.+?)(?:-\d+)?$/);
        const blockType = match ? match[1] : (el.tagName === 'HEADER' ? 'header' : el.tagName === 'FOOTER' ? 'footer' : '');

        // Usar data-anim existente o asignar default
        const animValue = el.getAttribute('data-anim') || 'fade-up';

        // Crear wrapper que replica la estructura del editor
        const wrapper = document.createElement('div');
        wrapper.setAttribute('data-anim', animValue);
        wrapper.setAttribute('data-type', el.getAttribute('data-type') || blockType);
        wrapper.setAttribute('data-index', String(index));

        // El data-anim se MUEVE al wrapper (no se copia): el HTML compilado
        // (viewer.js → compileSiteHtml) lo inyecta en la propia <section>/<footer>, pero
        // el editor (render.js) lo pone SOLO en el wrapper. Dejarlo en ambos rompía el
        // publicado de dos formas, ambas invisibles en el editor:
        //   1) anime-orchestrador.js hace querySelectorAll('[data-anim]') → matcheaba
        //      wrapper Y section → cada bloque se animaba DOS veces, y la 2ª con la
        //      variante equivocada (su lookup querySelector('[class*="variant-v"]') no
        //      matchea al propio elemento → la section se autodetectaba siempre v1) →
        //      dos tweens escribiendo los mismos targets = texto congelado semi-invisible.
        //   2) Las reglas CSS de DESCENDENCIA de site.css ([data-anim]:not(.anime-init)
        //      .pz-card / .pz-svg-wrap svg, [data-anim].anime-init .logo-item) matchearían
        //      a través de la section, que ya nunca recibe .anime-init (solo lo recibe el
        //      wrapper) → process-zigzag y logos ocultos PARA SIEMPRE en el publicado.
        // Se quita DESPUÉS de leer data-anim/data-type arriba (el shim los necesita).
        el.removeAttribute('data-anim');

        // Copiar el fondo del bloque al wrapper (evitar gaps transparentes)
        const bgColor = el.style.getPropertyValue('--bg');
        if (bgColor) {
            wrapper.style.setProperty('--bg', bgColor);
            wrapper.style.backgroundColor = 'var(--bg)';
        }

        // RECORTE del separador (core/dividers.js): el wrapper hereda `data-recorte` y sus
        // variables, igual que hace render.js en el editor. NO es cosmético — es obligatorio
        // desde el momento en que este shim pinta el fondo del bloque: sin copiarlo, el wrapper
        // de la sección de abajo (sin recortar, y pintado el ÚLTIMO) taparía con su color plano
        // la cuña que la sección acaba de abrir, y el separador desaparecería del sitio
        // publicado. El `margin-top` negativo tampoco puede quedarse dentro: colapsaría con el
        // del wrapper y movería la caja equivocada. El CSS neutraliza el de la <section>
        // (`[data-recorte] > [data-recorte]`), así que la banda se cuenta UNA sola vez.
        const recorte = el.getAttribute('data-recorte');
        if (recorte) {
            wrapper.setAttribute('data-recorte', recorte);
            ['--recorte-h', '--recorte-h-abajo', '--recorte-clip'].forEach(v => {
                const valor = el.style.getPropertyValue(v);
                if (valor) wrapper.style.setProperty(v, valor);
            });
        }

        // Envolver: padre.insertBefore(wrapper, el) + wrapper.appendChild(el)
        el.parentNode.insertBefore(wrapper, el);
        wrapper.appendChild(el);
    });

    // ── HEADER HOIST ──
    // #resultado tiene container-type:inline-size que atrapa position:fixed.
    // Movemos el <header> (o su wrapper) FUERA de #resultado para que
    // position:fixed funcione contra el viewport real.
    const headerWrapper = root.querySelector('[data-type="header"]');
    if (headerWrapper) {
        const headerEl = headerWrapper.querySelector('header');
        if (headerEl) {
            // Mover el header directamente antes de #resultado
            root.parentNode.insertBefore(headerEl, root);
            // Limpiar el wrapper vacío que queda dentro
            if (headerWrapper.parentNode) headerWrapper.parentNode.removeChild(headerWrapper);
            // PARIDAD con render.js (preview): evita que el header fixed tape la 1ª
            // sección no-hero de la página publicada (subpáginas: form/menu/content…).
            applyFirstSectionHeaderOffset(headerEl);
        }
    }

    // ── LANDMARKS DE ACCESIBILIDAD (<main> + "saltar al contenido") ──
    // Va aquí, al final del shim y DESPUÉS del hoist del header: el <main> tiene que envolver
    // el contenido y NO el header (ya fuera de #resultado) ni el <footer>. La función es la
    // MISMA que usa el preview del editor (core/render.js::applyPublicLandmarks) → una sola
    // implementación para las dos superficies servidas. Al hacerse en runtime, también arregla
    // los sitios YA publicados sin necesidad de republicarlos.
    applyPublicLandmarks(t('skip_content'));
    });
})();

// ═══════════════════════════════════════════════════════════════
//  DESACTIVAR CONTENTEDITABLE: El HTML compilado conserva
//  contenteditable="true" del editor. En la página pública lo
//  desactivamos para evitar que los visitantes puedan editar texto.
// ═══════════════════════════════════════════════════════════════
document.querySelectorAll('[contenteditable="true"]').forEach(el => {
    el.setAttribute('contenteditable', 'false');
});

// ═══════════════════════════════════════════════════════════════
//  ROUTER PÚBLICO: convierte ?page=X a navegación real.
//  En el editor, las páginas usan URLs SPA (?page=slug).
//  En el sitio público, cada página es un SitePage con URL
//  real (/slug/). Este router intercepta los clicks y redirige.
// ═══════════════════════════════════════════════════════════════
document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="?page="]');
    if (!link) return;
    // `?page=<id>` o `?page=<id>#<ancla>` (el pie común enlaza así una sección de la home desde
    // las subpáginas, utils/pie-enlaces.js). Un enlace EXTERNO que lleve «?page=» en su consulta
    // no es de este sitio: se deja al navegador.
    const destino = destinoDePagina(link.getAttribute('href'));
    if (!destino) return;

    e.preventDefault();
    const pageSlug = destino.id;
    const ancla = destino.ancla ? '#' + destino.ancla : '';

    // Calcular la base de la URL pública según cómo se resuelve el tenant (ver web/middleware.py):
    //  - Tenant en SUBDOMINIO (p.ej. "empresa-4.nube3i.com", "empresa-4.<ip>.nip.io"):
    //    las páginas viven en la raíz  ->  base '/'.
    //  - Tenant en el PATH (p.ej. "localhost:8000/empresa/..."):
    //    la primera carpeta es el tenant  ->  base '/<carpeta>/'.
    const host = window.location.hostname;
    const hostParts = host.split('.');
    const isNube3iDomain = host.endsWith('nube3i.com') || host.endsWith('.nip.io') || host.endsWith('.sslip.io');
    const isLocalhost = host === 'localhost' || host === '127.0.0.1';
    // Subdominio de tenant: host con >2 etiquetas y primera distinta de 'www'.
    const tenantInSubdomain = isNube3iDomain && hostParts.length > 2 && hostParts[0] !== 'www';

    let base = '/';
    if (!tenantInSubdomain && (isNube3iDomain || isLocalhost)) {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        if (pathParts.length > 0) {
            base = '/' + pathParts[0] + '/';
        }
    }

    if (pageSlug && pageSlug !== 'home') {
        window.location.href = base + pageSlug + '/' + ancla;
    } else {
        window.location.href = base + ancla;
    }
});

// --- INICIALIZAR DEPENDENCIAS DE VENDOR ---
initSmoothScroll();
initSplideCarousels();
initBlockActions();

// --- RESOLVER IMÁGENES Y EJECUTAR HOOKS DE ANIMACIÓN ---
setTimeout(() => {
    resolverImagenesUnsplash();
    runPublicPostRenderHooks();
}, 100);

